<template>
  <section
    class="lg:min-h-[500px] col-start-1 col-end-[15] overflow-hidden mt-20 lg:mt-34 grid grid-cols-layout-mobile auto-rows-min gap-x-5 md:gap-x-8 lg:grid-cols-layout lg:gap-x-12 2xl:gap-x-20"
  >
    <div
      class="relative z-30 col-span-full flex overflow-x-auto no-scrollbar 2xl:row-1 2xl:col-span-10 2xl:mb-8 2xl:overflow-x-visible"
    >
      <filter-tag
        v-for="tag in tags"
        :label="tag.title"
        :selected="tag.id === activeFilter"
        :theme="themeType"
        classes="mr-4 shrink-0 first:mx-5 md:first:ml-8 md:last:mr-8 lg:first:ml-12 lg:last:mr-[108px]  2xl:first:ml-20"
        @handleClick="activeFilter = tag.id"
      />
    </div>
    <dropdown-component
      :label="dropdownLabel"
      :options="options"
      :theme="themeType"
      :selected-option="selectedOption"
      classes="col-span-8 col-start-6 2xl:col-start-12 2xl:col-span-2 2xl:row-1 2xl:mb-8"
      isNewsFilter
      @selectOption="fetchNewsWithFilter"
    />
    <div v-if="newsCards" class="col-start-2 col-span-12 mt-6 mb-20 md:mb-24">
      <news-card-grid :cards="newsCards" :theme="theme" />
    </div>
    <div
      v-if="showLoadMore"
      class="col-start-6 col-span-4 md:col-span-2 md:col-start-7 mb-36"
    >
      <custom-button
        label="Load more"
        theme="light"
        type="secondary"
        @handleClick="handleLoadMore"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { toRefs, onMounted, ref, watch, computed } from "vue";
import CustomButton from "@atoms/Button/CustomButton.ce.vue";
import DropdownComponent, { Option } from "@atoms/Dropdown/Dropdown.ce.vue";
import NewsCardGrid from "@organisms/NewsCardGrid/NewsCardGrid.ce.vue";
import { getNews, getYearsFilter, getTranslations, getFilterTags } from "@/util/api";
import { IFilterTag, IYear, Theme } from "@/types/types";
import { defineProps } from "vue";
import { NewCardProps } from "@/components/Molecules/NewsCard/NewsCard.ce.vue";
import { changeTheme, formatDate, getThemeType } from "@/util/helper";
import { INewsPage } from "@/types/INewsPage";
import { FilterTagType } from "@/util/constants";
import FilterTag from "@components/Atoms/FilterTag/FilterTag.ce.vue";

const props = defineProps<{
  pageId?: string;
  theme?: Theme | null;
}>();
const { pageId, theme } = toRefs(props);

const tags = ref<IFilterTag[] | null>(null);
const pageNumber = ref(1);
const pageSize = ref(18);
const activeFilter = ref("");
const newsCards = ref<NewCardProps[]>([]);
const dropdownLabel = ref<string>("");
const yearFilters = ref<IYear[]>([]);
const selectedYearId = ref<string | null>(null);
const mostRecentTranslation = ref<string>("");
const mostRecentYearOption = ref<Option | null>(null);
const selectedOption = ref<Option | null>(null);

onMounted(async () => {
  tags.value = await getFilterTags(FilterTagType.NEWSTAG);
  activeFilter.value = tags.value[0].id;
  dropdownLabel.value = await getTranslations("sortby");
  mostRecentTranslation.value = await getTranslations("currentyearlabel");
  const years = await fetchYearFilter();
  if (!years) return;
  yearFilters.value = years;

  mostRecentYearOption.value = {
    [mostRecentTranslation.value]: "currentyearlabel",
  };
  selectedOption.value = {
    [mostRecentTranslation.value]: "currentyearlabel",
  };

  if (activeFilter.value) {
    const filteredNews = await fetchNews();

    if (!filteredNews) return;
    newsCards.value = createNewsCards(filteredNews);
  }

  if (theme?.value) {
    changeTheme(theme.value);
  }
});

watch(activeFilter, (value) => {
  if (value) {
    pageNumber.value = 1;
    selectedOption.value = mostRecentYearOption.value;
    selectedYearId.value = null;
    fetchNews().then((news) => {
      if (!news) return;
      newsCards.value = createNewsCards(news);
    });
    fetchYearFilter().then((years) => {
      if (!years) return;
      yearFilters.value = years;
    });
  }
});

async function fetchYearFilter() {
  if (pageId?.value) {
    const years = await getYearsFilter(pageId.value, activeFilter.value);
    return years;
  }
}

async function fetchNews() {
  if (!pageId?.value) return;
  if (selectedYearId.value && selectedYearId.value !== "currentyearlabel") {
    const news = await getNews(
      pageId.value,
      activeFilter.value,
      pageNumber.value,
      pageSize.value,
      selectedYearId.value
    );
    return news;
  }
  const news = await getNews(
    pageId.value,
    activeFilter.value,
    pageNumber.value,
    pageSize.value
  );
  return news;
}

function createNewsCards(news: INewsPage[]) {
  const newsCards = news.map((n: INewsPage) => {
    const date = formatDate(n.properties.newsDate);
    return {
      image: JSON.stringify(n.properties.image[0]),
      imgAlt: n.properties.imageAltText,
      location: n.properties.newsRegion[0].name,
      date: date,
      title: n.name,
      href: n.route.path,
      theme: theme?.value,
    };
  });
  return newsCards;
}

async function handleLoadMore() {
  pageNumber.value++;
  const addedNews = await fetchNews();
  if (!addedNews) return;
  const addedNewsCards = createNewsCards(addedNews);
  newsCards.value = [...newsCards.value, ...addedNewsCards];
}

const showLoadMore = computed(() => {
  return newsCards.value.length === pageSize.value;
});

const themeType = computed(() => {
  if (!theme?.value) return;
  return getThemeType(theme.value);
});

const options = computed(() => {
  const yearOptions = yearFilters.value.map((year) => {
    return {
      [year.title]: year.guid,
    };
  });
  if (!mostRecentYearOption.value) return yearOptions;
  return yearOptions.concat(mostRecentYearOption.value);
});

async function fetchNewsWithFilter(option: Option) {
  selectedYearId.value = Object.values(option)[0];
  selectedOption.value = option;
  // this needs to be tested
  pageNumber.value = 1;
  const filteredNews = await fetchNews();
  if (!filteredNews) return;
  newsCards.value = createNewsCards(filteredNews);
}
</script>
